import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import nurseService from 'src/api/nurse/nurseService';
import ByzMuiOpenProcessMenu from 'src/components/core-components/mui/ByzMuiOpenProcessMenu';
import OpenComponentDialog from 'src/components/shared-components/modal/openComponentModal';
import { useTranslate } from 'src/locales';
import BlondDetail from './blond-detail';
import CancelDialog from 'src/components/core-components/mui/CancalDialog';
import dayjs from 'dayjs';
import type { CardItemProps } from './card-item.types';

export const BloodCard: React.FC<CardItemProps> = ({ data, patientId, refetch }) => {
  const { t } = useTranslate();
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'success',
  });

  const showSnackbar = (message: string, type: 'success' | 'error' = 'success', title?: string) => {
    setSnackbar({
      open: true,
      message,
      type,
      title: title || (type === 'success' ? t('success') : t('error')),
    });
  };

  const handleOpenDetail = () => {
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  const handleUpdateStatus = async (status: string | number, message?: string) => {
    const srnid = data?.othertreatment?.srnid;
    const udid = data?.othertreatment?.udid;
    if (!srnid || !udid) {
      showSnackbar('SrnId veya UdId bulunamadı.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await nurseService.order.setStatusOtherTreatments<{ success: boolean; message: string }>({
        srnid,
        udid,
        status,
        message: message?.trim() ? message.trim() : undefined,
      });

      if ((response as any)?.success || typeof response === 'number') {
        showSnackbar((response as any)?.message || 'İşlem başarıyla tamamlandı.', 'success');
        refetch?.();
      } else {
        showSnackbar((response as any)?.message || 'İşlem başarısız.', 'error');
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.response?.data?.error || 'Bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancelDialog = () => {
    if (loading) return;
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async (reason: string) => {
    await handleUpdateStatus(2, reason);
    setCancelDialogOpen(false);
  };

  const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const cardClickHandler =
    detailOpen || loading || cancelDialogOpen || snackbar.open
      ? undefined
      : handleOpenDetail;

  return (
    <Box
      sx={{
        position: 'relative',
        boxShadow: '0px 1px 2px #e0e5eb, 0px 0px 0px 1px #e0e5eb',
        borderRadius: '8px',
        backgroundColor: '#fff',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0px 0px 10px',
        boxSizing: 'border-box',
        gap: '4px',
        textAlign: 'left',
        fontSize: '12px',
        color: '#767a85',
        fontFamily: 'Poppins',
        cursor: cardClickHandler ? 'pointer' : 'default',
      }}
      onClick={cardClickHandler}
    >
      {/* Üst çizgi - blood tipi için mor */}
      <Box
        sx={{
          width: '286px',
          height: '4px',
          position: 'relative',
          backgroundColor: '#e0a910',
        }}
      />
      <Box
        sx={{
          width: '270px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '5px',
        }}
      >
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            gap: '68px',
            flexShrink: 0,
            color: '#2f353d',
          }}
        >
          <Box
            sx={{
              width: '140px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                alignSelf: 'stretch',
                position: 'relative',
                letterSpacing: '-0.01em',
                lineHeight: '140%',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {(data?.othertreatment?.name?.trim() && data.othertreatment.name.trim() !== '')
                ? data.othertreatment.name.trim()
                : (data?.othertreatment?.srntype?.trim() && data.othertreatment.srntype.trim() !== '')
                  ? data.othertreatment.srntype.trim()
                  : 'Kan Ürünü'}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
            onClick={stopPropagation}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: '1px solid #e0a910',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.6,
                pointerEvents: 'none',
              }}
            >
              <img
                src="/assets/icons/print/print.svg"
                alt="Print"
                style={{ width: '16px', height: '16px' }}
              />
            </div>
            <ByzMuiOpenProcessMenu
              menuItems={(() => {
                const isApplied = data?.othertreatment?.ismake === '1';
                return [
                  {
                    label: t('nurse.order-medicine.menu-items.cannot-apply'),
                    onClick: () => handleUpdateStatus(0),
                    disabled: isApplied,
                  },
                  {
                    label: t('nurse.order-medicine.menu-items.undo-application'),
                    onClick: () => handleUpdateStatus(0),
                    divider: true,
                    disabled: !isApplied,
                  },
                  {
                    label: t('nurse.order-medicine.menu-items.terminate-cancel'),
                    onClick: handleOpenCancelDialog,
                    disabled: isApplied,
                  },
                ];
              })()}
              hideHeaderTitle
              sx={{ p: 0 }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: '271px',
            height: '1px',
            position: 'relative',
            borderTop: '1px solid #e6e6e6',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />

        {/* Tarih ve saat bilgileri */}
        <Box
          sx={{
            width: '272px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            fontSize: '13px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '29px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('nurse.order-medicine.card-fields.start-time')}
                  </Box>
                  <Box
                    sx={{
                      alignSelf: 'stretch',
                      position: 'relative',
                      fontSize: '12px',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 500,
                      color: '#1f2026',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {data?.othertreatment?.startdate && data.othertreatment.startdate.trim() !== ''
                      ? dayjs(data.othertreatment.startdate).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.application-time')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {data?.othertreatment?.startdate && data.othertreatment.startdate.trim() !== ''
                    ? dayjs(data.othertreatment.startdate).format('HH:mm')
                    : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '271px',
            height: '1px',
            position: 'relative',
            borderTop: '1px solid #e6e6e6',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            width: '272px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
            fontSize: '13px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '29px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: '2px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('nurse.order-medicine.card-fields.end-time')}
                  </Box>
                  <Box
                    sx={{
                      alignSelf: 'stretch',
                      position: 'relative',
                      fontSize: '12px',
                      letterSpacing: '-0.01em',
                      lineHeight: '22px',
                      fontWeight: 500,
                      color: '#1f2026',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {data?.othertreatment?.finishdate && data.othertreatment.finishdate.trim() !== ''
                      ? dayjs(data.othertreatment.finishdate).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.blood-group')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(data?.othertreatment?.bloodgroupname?.trim() && data.othertreatment.bloodgroupname.trim() !== '')
                    ? data.othertreatment.bloodgroupname.trim()
                    : '-'}
                </Box>
              </Box>
              <Box
                sx={{
                  width: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '2px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.amount')}
                </Box>
                <Box
                  sx={{
                    alignSelf: 'stretch',
                    position: 'relative',
                    fontSize: '12px',
                    letterSpacing: '-0.01em',
                    lineHeight: '22px',
                    fontWeight: 500,
                    color: '#1f2026',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(data?.othertreatment?.quantity?.trim() && data.othertreatment.quantity.trim() !== '')
                    ? `${data.othertreatment.quantity.trim()} Ünite`
                    : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Blood tipi için tek mor Uygula butonu */}
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '5px',
            flexShrink: 0,
            color: '#2979c3',
          }}
        >
          {(() => {
            const isApplied = data?.othertreatment?.ismake === '1';
            return (
              <Box
                sx={{
                  width: '100%',
                  height: '27px',
                  borderRadius: '100px',
                  backgroundColor: isApplied ? 'rgba(3, 202, 26, 0.1)' : 'rgba(224, 169, 16, 0.1)',
                  border: isApplied ? '1px solid #03ca1a' : '1px solid #e0a910',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2px 4px',
                  color: isApplied ? '#03ca1a' : '#e0a910',
                  opacity: loading ? 0.6 : 1,
                  pointerEvents: (loading || isApplied) ? 'none' : 'auto',
                  cursor: isApplied ? 'default' : 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(1);
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    letterSpacing: '-0.02em',
                    lineHeight: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {loading && <CircularProgress size={14} color="inherit" />}
                  {isApplied ? 'Uygulandı' : t('nurse.order-medicine.buttons.apply')}
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      <OpenComponentDialog
        open={detailOpen}
        onClose={handleCloseDetail}
        title={t('nurse.order-medicine.detail.blood-detail')}
        showPatientData={false}
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxWidth: '1200px',
            width: '90%',
          },
        }}
      >
        <BlondDetail onClose={handleCloseDetail} srnId={data?.othertreatment?.srnid} udId={data?.othertreatment?.udid} />
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={snackbar.open}
        title={snackbar.title}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <CancelDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title={t("warning-Message")}
        description={t("nurse.order-medicine.dialogs.cancel-request.description")}
        placeholder={t("description-required")}
        cancelText={t("no")}
        confirmText={t("yes")}
        requireInput={true}
        loading={loading}
        width="650px"
        height="420px"
      />
    </Box >
  );
};

